import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, Star, AlertTriangle, Loader2 } from 'lucide-react';
import ComposerSheet from './ui/ComposerSheet';
import { Button } from './ui/button';
import { getEventTheme } from '../services/eventThemeService';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * CompletionFlow — Staged ComposerSheet for end-of-job experience.
 *
 * Stages:
 *   CONFIRM  → customer decides (confirm vs report problem)
 *   REVIEW   → optional rating + comment (funds already released)
 *   DISPUTE  → problem description (funds held)
 *   DONE     → success/dispute confirmation, auto-dismiss
 */
export default function CompletionFlow({ open, onClose, booking, vendorName, eventType, onCompleted }) {
  const [stage, setStage] = useState('confirm');
  const [loading, setLoading] = useState(false);

  // Review state
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');

  // Dispute state
  const [disputeText, setDisputeText] = useState('');

  // Done state
  const [doneVariant, setDoneVariant] = useState('confirmed'); // 'confirmed' | 'disputed'

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const theme = getEventTheme(eventType);

  // Reset when opening
  useEffect(() => {
    if (open) {
      setStage('confirm');
      setLoading(false);
      setRating(0);
      setHoverRating(0);
      setComment('');
      setDisputeText('');
      setDoneVariant('confirmed');
    }
  }, [open]);

  // Auto-dismiss on DONE
  useEffect(() => {
    if (stage !== 'done') return;
    const delay = doneVariant === 'disputed' ? 4000 : 3000;
    const timer = setTimeout(() => {
      onClose();
      onCompleted?.();
    }, delay);
    return () => clearTimeout(timer);
  }, [stage, doneVariant, onClose, onCompleted]);

  const handleConfirm = useCallback(async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/bookings/${booking.booking_id}/confirm-completion`, {}, { headers });
      setStage('review');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to confirm completion');
    } finally {
      setLoading(false);
    }
  }, [booking, headers]);

  const handleSubmitReview = useCallback(async () => {
    if (rating === 0) { toast.error('Please select a rating'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/reviews`, {
        vendor_id: booking.vendor_id,
        inquiry_id: booking.inquiry_id,
        rating,
        comment: comment.trim() || undefined,
      }, { headers });
      setDoneVariant('confirmed');
      setStage('done');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit review');
    } finally {
      setLoading(false);
    }
  }, [booking, rating, comment, headers]);

  const handleSkipReview = useCallback(() => {
    setDoneVariant('confirmed');
    setStage('done');
  }, []);

  const handleSubmitDispute = useCallback(async () => {
    if (disputeText.trim().length < 20) {
      toast.error('Please provide at least 20 characters describing the issue');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/bookings/${booking.booking_id}/report-problem`, {
        description: disputeText.trim(),
      }, { headers });
      setDoneVariant('disputed');
      setStage('done');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit report');
    } finally {
      setLoading(false);
    }
  }, [booking, disputeText, headers]);

  if (!open || !booking) return null;

  const sheetTitle =
    stage === 'confirm' ? `How did it go with ${vendorName}?` :
    stage === 'review'  ? `Leave a review for ${vendorName}` :
    stage === 'dispute' ? 'What went wrong?' :
    doneVariant === 'confirmed' ? 'Thank you!' : "We've received your report";

  return (
    <ComposerSheet
      open={open}
      onClose={stage === 'done' ? () => { onClose(); onCompleted?.(); } : onClose}
      title={sheetTitle}
      testId="completion-flow-sheet"
      maxWidth="sm:max-w-md"
      footer={stage !== 'done' ? (
        <StageFooter
          stage={stage}
          loading={loading}
          rating={rating}
          disputeLength={disputeText.trim().length}
          accentColor={theme.accentColor}
          onConfirm={handleConfirm}
          onSubmitReview={handleSubmitReview}
          onSkipReview={handleSkipReview}
          onSubmitDispute={handleSubmitDispute}
          onBackToConfirm={() => setStage('confirm')}
        />
      ) : null}
    >
      {stage === 'confirm' && (
        <ConfirmStage
          vendorName={vendorName}
          accentColor={theme.accentColor}
          profileImage={booking.profile_image}
          loading={loading}
          onConfirm={handleConfirm}
          onReportProblem={() => setStage('dispute')}
        />
      )}
      {stage === 'review' && (
        <ReviewStage
          vendorName={vendorName}
          rating={rating}
          hoverRating={hoverRating}
          comment={comment}
          accentColor={theme.accentColor}
          onRate={setRating}
          onHover={setHoverRating}
          onComment={setComment}
        />
      )}
      {stage === 'dispute' && (
        <DisputeStage
          disputeText={disputeText}
          onChange={setDisputeText}
        />
      )}
      {stage === 'done' && (
        <DoneStage variant={doneVariant} vendorName={vendorName} accentColor={theme.accentColor} />
      )}
    </ComposerSheet>
  );
}


/* ── Stage: Confirm ── */
function ConfirmStage({ vendorName, accentColor, profileImage, loading, onConfirm, onReportProblem }) {
  const [imgFailed, setImgFailed] = useState(false);
  const showImg = profileImage && !imgFailed;
  return (
    <div className="flex flex-col items-center text-center py-4" data-testid="completion-confirm-stage">
      <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mb-4 overflow-hidden">
        {showImg ? (
          <img
            src={profileImage}
            alt={vendorName}
            className="w-16 h-16 rounded-2xl object-cover"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <span className="text-2xl font-bold text-gray-400">{(vendorName || '?')[0]}</span>
        )}
      </div>
      <p className="text-base font-semibold text-gray-900 mb-1">{vendorName}</p>
      <p className="text-sm text-gray-500 mb-8">
        {vendorName} marked the job as complete. Did everything go well?
      </p>

      <button
        onClick={onConfirm}
        disabled={loading}
        className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl text-white font-semibold text-sm transition-all active:scale-[0.98]"
        style={{ backgroundColor: accentColor }}
        data-testid="completion-confirm-btn"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4.5 h-4.5" />}
        {loading ? 'Confirming...' : 'Everything went great'}
      </button>

      <button
        onClick={onReportProblem}
        className="mt-4 text-sm text-gray-400 hover:text-gray-600 transition-colors"
        data-testid="completion-report-problem-btn"
      >
        Report a problem
      </button>
    </div>
  );
}


/* ── Stage: Review ── */
function ReviewStage({ vendorName, rating, hoverRating, comment, accentColor, onRate, onHover, onComment }) {
  const activeRating = hoverRating || rating;
  return (
    <div className="py-2" data-testid="completion-review-stage">
      <p className="text-sm text-gray-500 text-center mb-6">
        Payment has been released. Your review helps other planners.
      </p>

      {/* Star rating */}
      <div className="flex justify-center gap-2 mb-6" data-testid="completion-star-rating">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            onClick={() => onRate(star)}
            onMouseEnter={() => onHover(star)}
            onMouseLeave={() => onHover(0)}
            className="p-1 transition-transform hover:scale-110 active:scale-95"
            data-testid={`star-${star}`}
          >
            <Star
              className={`w-9 h-9 transition-colors ${
                star <= activeRating ? 'fill-current' : 'text-gray-200'
              }`}
              style={star <= activeRating ? { color: accentColor } : {}}
            />
          </button>
        ))}
      </div>

      {/* Comment */}
      <textarea
        value={comment}
        onChange={(e) => onComment(e.target.value)}
        placeholder="Share your experience... (optional)"
        className="w-full border border-gray-200 rounded-xl px-3.5 py-3 text-sm min-h-[100px] resize-none focus:ring-2 focus:ring-gray-200 focus:border-gray-300 outline-none transition-all"
        data-testid="completion-review-comment"
      />
    </div>
  );
}


/* ── Stage: Dispute ── */
function DisputeStage({ disputeText, onChange }) {
  return (
    <div className="py-2" data-testid="completion-dispute-stage">
      <textarea
        value={disputeText}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Please describe what went wrong (min 20 characters)..."
        className="w-full border border-gray-200 rounded-xl px-3.5 py-3 text-sm min-h-[120px] resize-none focus:ring-2 focus:ring-red-100 focus:border-red-300 outline-none transition-all"
        autoFocus
        data-testid="completion-dispute-input"
      />
      <p className="text-xs text-gray-400 mt-2">
        {disputeText.trim().length}/20 characters minimum
      </p>
      <div className="mt-4 p-3 bg-amber-50 border border-amber-100 rounded-lg">
        <p className="text-xs text-amber-700 flex items-start gap-2">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          Our team will review your report and hold funds until resolved.
        </p>
      </div>
    </div>
  );
}


/* ── Stage: Done ── */
function DoneStage({ variant, vendorName, accentColor }) {
  return (
    <div className="flex flex-col items-center text-center py-8" data-testid="completion-done-stage">
      {variant === 'confirmed' ? (
        <>
          <div className="w-14 h-14 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: accentColor + '18' }}>
            <CheckCircle className="w-7 h-7" style={{ color: accentColor }} />
          </div>
          <p className="text-base font-semibold text-gray-900 mb-1">Thank you!</p>
          <p className="text-sm text-gray-500">Payment has been released to {vendorName}.</p>
        </>
      ) : (
        <>
          <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center mb-4">
            <AlertTriangle className="w-7 h-7 text-amber-600" />
          </div>
          <p className="text-base font-semibold text-gray-900 mb-1">We've received your report</p>
          <p className="text-sm text-gray-500">Funds are held securely while our team reviews.</p>
        </>
      )}
    </div>
  );
}


/* ── Footer (per stage) ── */
function StageFooter({ stage, loading, rating, disputeLength, accentColor, onConfirm, onSubmitReview, onSkipReview, onSubmitDispute, onBackToConfirm }) {
  if (stage === 'confirm') return null; // Confirm stage has its CTA inline

  if (stage === 'review') {
    return (
      <div className="flex gap-3" data-testid="completion-review-footer">
        <Button
          variant="outline"
          onClick={onSkipReview}
          className="flex-1"
          data-testid="completion-skip-review-btn"
        >
          Skip
        </Button>
        <Button
          onClick={onSubmitReview}
          disabled={loading || rating === 0}
          className="flex-1 text-white"
          style={{ backgroundColor: accentColor }}
          data-testid="completion-submit-review-btn"
        >
          {loading ? 'Submitting...' : 'Submit Review'}
        </Button>
      </div>
    );
  }

  if (stage === 'dispute') {
    return (
      <div className="flex gap-3" data-testid="completion-dispute-footer">
        <Button
          variant="outline"
          onClick={onBackToConfirm}
          className="flex-1"
          data-testid="completion-dispute-back-btn"
        >
          Go Back
        </Button>
        <Button
          onClick={onSubmitDispute}
          disabled={loading || disputeLength < 20}
          className="flex-1 bg-red-600 hover:bg-red-700 text-white"
          data-testid="completion-submit-dispute-btn"
        >
          {loading ? 'Submitting...' : 'Submit Report'}
        </Button>
      </div>
    );
  }

  return null;
}
